/**
 * Tour Steps Configuration
 * PRD: PRD-product-tour-organisateur.md
 */

import React from 'react';
import { Step } from 'react-joyride';

/**
 * Desktop Tour Steps - using React Joyride spotlight
 */
export const desktopTourSteps: Step[] = [
  {
    target: '.organizer-desktop-layout__create-button',
    content: (
      <div className="tour-tooltip">
        <h3>Create Your Events</h3>
        <p>You've probably spotted this already! 😊 The <strong>Create Event</strong> button gives you two options: add events manually or <strong>import</strong> them from Instagram, a photo, PDF, or Excel!</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '.organizer-desktop-layout__kpis-section',
    content: (
      <div className="tour-tooltip">
        <h3>Track Your Performance</h3>
        <p>Here's where the magic happens! ✨ See how many times your events are seen! Track your reach and switch between time periods anytime!</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '.organizer-desktop-layout__tabs',
    content: (
      <div className="tour-tooltip">
        <h3>Organize Your Events</h3>
        <p>Keep things tidy! 📁<br /><strong>Active</strong> = live & visible to tourists,<br /><strong>Drafts</strong> = work in progress,<br /><strong>Past</strong> = events that have ended.</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '.view-toggle',
    content: (
      <div className="tour-tooltip">
        <h3>Planning View</h3>
        <p>Switch between <strong>list</strong> and <strong>calendar</strong> view! 📅 The planning view shows your week at a glance — drag events to reschedule, resize to change duration, and click empty slots to create!</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: 'body',
    content: (
      <div className="tour-tooltip">
        <h3>Instagram Posts</h3>
        <p>Need to promote your events? 📸 Click the <strong>Instagram icon</strong> on any event card to generate a beautiful post ready to share! Works for single and recurring events.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '.organizer-desktop-layout__sidebar',
    content: (
      <div className="tour-tooltip">
        <h3>Navigation</h3>
        <p>Your command center! 🧭 Update your <strong>Profile</strong>, peek at the <strong>Public Space</strong> to see what tourists see, or reach out anytime!</p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: 'body',
    content: (
      <div className="tour-tooltip tour-tooltip--final">
        <h3>You're Ready!</h3>
        <p>You're all set! 🎉 Quick tip: use the event menu to <strong>duplicate</strong> or <strong>cancel</strong>.<br />And hey — each event card shows how many times it was seen!</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

/**
 * Mobile Slide interface for carousel
 */
export interface MobileSlide {
  icon: string;
  title: string;
  description: React.ReactNode;
}

/**
 * Mobile Tour Slides - carousel format
 */
export const mobileSlides: MobileSlide[] = [
  {
    icon: 'CalendarPlus',
    title: 'Create Your Events',
    description: (
      <>
        You've probably spotted this already! 😊 Tap the <strong>Create Event</strong> button above the navigation bar — it opens two options: add events manually or <strong>import</strong> them from Instagram, a photo, PDF, or Excel!
      </>
    ),
  },
  {
    icon: 'BarChart',
    title: 'Track Your Performance',
    description: (
      <>
        Tap <strong>Dashboard</strong> below — here's where the magic happens! ✨ See how many times your events are seen and track your reach over time!
      </>
    ),
  },
  {
    icon: 'FolderOpen',
    title: 'Organize Your Events',
    description: (
      <>
        Keep things tidy! 📁<br />
        <strong>Active</strong> = live & visible to tourists,<br />
        <strong>Drafts</strong> = work in progress,<br />
        <strong>Past</strong> = events that have ended.
      </>
    ),
  },
  {
    icon: 'CalendarDays',
    title: 'Planning View',
    description: (
      <>
        Switch between <strong>list</strong> and <strong>calendar</strong> view! 📅 Tap the toggle next to "My Events" to see your week at a glance!
      </>
    ),
  },
  {
    icon: 'Instagram',
    title: 'Instagram Posts',
    description: (
      <>
        Need to promote your events? 📸 Tap the <strong>Instagram icon</strong> on any event card to generate a beautiful post ready to share!
      </>
    ),
  },
  {
    icon: 'Compass',
    title: 'Navigation',
    description: (
      <>
        Easy access! 🧭 Your <strong>Profile</strong> is one tap away.<br />
        In the <strong>Menu</strong>, find Public Space, Help & more!
      </>
    ),
  },
  {
    icon: 'Rocket',
    title: "You're Ready!",
    description: (
      <>
        You're all set! 🎉 Quick tip: use the event menu to <strong>duplicate</strong> or <strong>cancel</strong>.<br />
        And hey — each event card shows how many times it was seen!
      </>
    ),
  },
];
